import type { Application, Currency, RejectReason, SalaryPeriod, Stage, WorkModeType } from '../types'
import { supabase } from './supabaseClient'

type DbApplication = {
  id: string
  user_id: string | null
  company: string
  company_id: string | null
  position_title: string | null
  source_url: string
  expected_salary: number | null
  expected_hourly: number | null
  salary_min: number | null
  salary_max: number | null
  salary_currency: string | null
  salary_period: string | null
  target_amount: number | null
  target_period: string | null
  target_currency: string | null
  tech_stack: string[] | null
  contract_type: string | null
  benefits: string | null
  work_mode: string | null
  office_days: number | null
  process_stages: Stage[]
  status: string
  reject_reason: RejectReason | null
  archived: boolean | null
  favorite: boolean | null
  note: string | null
  follow_up_at: string | null
  offer_decision_reason: string | null
  raw_markdown: string | null
  is_draft: boolean | null
  created_at: string
  updated_at: string
  // Joined from company table
  company_info?: { info: string | null } | null
}

const toApp = (row: DbApplication): Application => ({
  id: row.id,
  company: row.company,
  companyId: row.company_id ?? null,
  companyInfo: row.company_info?.info ?? null,
  positionTitle: row.position_title ?? null,
  sourceUrl: row.source_url,
  expectedSalary: row.expected_salary,
  expectedHourly: row.expected_hourly,
  targetAmount: row.target_amount,
  targetPeriod: (row.target_period as SalaryPeriod) ?? null,
  targetCurrency: (row.target_currency as Currency) ?? 'PLN',
  salaryRange:
    row.salary_min != null && row.salary_max != null
      ? {
          min: row.salary_min,
          max: row.salary_max,
          currency: row.salary_currency ?? 'PLN',
          period: (row.salary_period as SalaryPeriod) ?? 'monthly',
        }
      : null,
  techStack: row.tech_stack ?? [],
  contractType: row.contract_type,
  benefits: row.benefits,
  workMode: row.work_mode as WorkModeType,
  officeDays: row.office_days ?? null,
  processStages: row.process_stages ?? [],
  status: row.status as Application['status'],
  archived: row.archived ?? false,
  favorite: row.favorite ?? false,
  note: row.note,
  followUpAt: row.follow_up_at ?? null,
  rejectReason: row.reject_reason ?? undefined,
  offerDecisionReason: row.offer_decision_reason ?? null,
  rawMarkdown: row.raw_markdown ?? undefined,
  isDraft: row.is_draft ?? false,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
})

export const fetchApplications = async (userId: string) => {
  const { data, error } = await supabase
    .from('applications')
    .select('*, company_info:company(info)')
    .eq('user_id', userId)
    .eq('is_draft', false)
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data as DbApplication[]).map(toApp)
}

export const insertApplication = async (userId: string, app: Application) => {
  const payload = {
    id: app.id,
    user_id: userId,
    company: app.company,
    company_id: app.companyId ?? null,
    position_title: app.positionTitle ?? null,
    source_url: app.sourceUrl,
    expected_salary: app.expectedSalary,
    expected_hourly: app.expectedHourly,
    salary_min: app.salaryRange?.min ?? null,
    salary_max: app.salaryRange?.max ?? null,
    salary_currency: app.salaryRange?.currency ?? null,
    salary_period: app.salaryRange?.period ?? null,
    target_amount: app.targetAmount ?? null,
    target_period: app.targetPeriod ?? null,
    target_currency: app.targetCurrency ?? null,
    tech_stack: app.techStack,
    contract_type: app.contractType ?? null,
    benefits: app.benefits ?? null,
    work_mode: app.workMode ?? null,
    office_days: app.workMode === 'hybrid' ? (app.officeDays ?? null) : null,
    process_stages: app.processStages,
    status: app.status,
    archived: app.archived ?? false,
    favorite: app.favorite ?? false,
    note: app.note ?? null,
    follow_up_at: app.followUpAt ?? null,
    reject_reason: app.rejectReason ?? null,
    offer_decision_reason: app.offerDecisionReason ?? null,
    raw_markdown: app.rawMarkdown ?? null,
    is_draft: app.isDraft ?? false,
    created_at: app.createdAt,
    updated_at: app.updatedAt,
  }
  const { data, error } = await supabase.from('applications').insert(payload).select('id').single()
  if (error) throw error
  return data
}

export const updateApplication = async (app: Application) => {
  const payload = {
    company: app.company,
    position_title: app.positionTitle ?? null,
    source_url: app.sourceUrl,
    expected_salary: app.expectedSalary,
    expected_hourly: app.expectedHourly,
    salary_min: app.salaryRange?.min ?? null,
    salary_max: app.salaryRange?.max ?? null,
    salary_currency: app.salaryRange?.currency ?? null,
    salary_period: app.salaryRange?.period ?? null,
    target_amount: app.targetAmount ?? null,
    target_period: app.targetPeriod ?? null,
    target_currency: app.targetCurrency ?? null,
    tech_stack: app.techStack,
    contract_type: app.contractType ?? null,
    benefits: app.benefits ?? null,
    work_mode: app.workMode ?? null,
    office_days: app.workMode === 'hybrid' ? (app.officeDays ?? null) : null,
    process_stages: app.processStages,
    status: app.status,
    archived: app.archived ?? false,
    favorite: app.favorite ?? false,
    note: app.note ?? null,
    follow_up_at: app.followUpAt ?? null,
    reject_reason: app.rejectReason ?? null,
    offer_decision_reason: app.offerDecisionReason ?? null,
    raw_markdown: app.rawMarkdown ?? null,
    is_draft: app.isDraft ?? false,
    updated_at: app.updatedAt,
  }
  const { error } = await supabase.from('applications').update(payload).eq('id', app.id)
  if (error) throw error
}

export const deleteApplicationDb = async (id: string) => {
  const { error } = await supabase.from('applications').delete().eq('id', id)
  if (error) throw error
}

export const deleteAccount = async (email: string, reason: string, details?: string) => {
  const { data, error } = await supabase.functions.invoke<{ ok: boolean }>('delete-account', {
    body: { email, reason, details },
  })

  if (error) {
    const message = typeof error === 'string' ? error : error.message ?? 'Failed to delete account.'
    throw new Error(message)
  }
  if (!data?.ok) {
    throw new Error('Failed to delete account.')
  }
}

export const updateLastActive = async (userId: string) => {
  const { error } = await supabase
    .from('user_profile')
    .update({ last_active_at: new Date().toISOString() })
    .eq('user_id', userId)

  if (error) {
    console.error('updateLastActive failed', error)
  }
}

